import {
  useMemo,
  useState,
  useEffect,
} from 'react'
import {
  continueRender, useVideoConfig, delayRender,
  AbsoluteFill, Img,
} from 'remotion'
import Subtitle, { Subtitle as SubtitleType } from './Subtitle'
import lodash from 'lodash'
import type { CirculoPresetId } from '@/lib/layouts/types'

// Type definitions
interface Word {
  word: string
  endTime: string
  startTime: string
  probability: number
}

interface SubtitleSegment {
  id?: number
  text?: string
  words: Word[]
  endTime: string
  startTime: string
}

interface Transcription {
  segments: SubtitleSegment[]
}

interface ReorganizeOptions {
  maxWordsPerLine: number
  maxSecondsPerLine: number
  maxCharactersPerLine: number
  cutPhrases: boolean
  removePunctuation: boolean
}

export interface PresetChange {
  type: 'set-value' | 'reorganize-words'
  attribute?: string
  value?: any
  settings?: ReorganizeOptions
}

interface SubtitleLineStyle {
  padding: number
  fontName: string
  fontColor: string
  lineHeight: number
  shadowSize: number
  shadowColor: string
  borderRadius: number
  textTransform: string
  backgroundColor: string
  enableBackground: boolean
  highlightCurrentWord: boolean
  fontSize: number
  verticalMargin: number
  horizontalMargin: number
  paddingX: number
  paddingY: number
  gap: number
  justifyContent: string
  alignItems: string
}

interface SubtitleHighlightStyle {
  fontColor: string
}

interface SubtitleHighlightBoxStyle {
  enabled: boolean
  paddingX: number
  paddingY: number
  borderRadius: number
  backgroundColor: string
}

interface Subtitle {
  highlightStyle: SubtitleHighlightStyle
  minSilenceToCut: number
  highlightBoxStyle: SubtitleHighlightBoxStyle
  keepOnlySubtitles: boolean
  extraTimeTolerance: number
  minSubtitleSilenceToCut: number
  animation: any
  lineStyle: SubtitleLineStyle
  transcription: Transcription
  reorganizeOptions?: ReorganizeOptions
}

interface VideoWithFontsProps {
  preset: PresetChange[]
  // Optional preview background (e.g. the user's video thumbnail) so the
  // preview can approximate a frame of the final render. `null`/undefined
  // preserves today's black-background behavior byte-for-byte.
  backgroundImageUrl?: string | null
  /** Solid canvas behind captions when there is no background image. */
  canvasBackgroundColor?: string
  curve?: { position: CirculoPresetId }
}

// How long we allow the manual background-image preload to try before
// giving up and falling back to the black background. Kept well under
// Remotion's default delayRender timeout (30s) so a dead URL degrades
// gracefully instead of stalling/cancelling the whole render.
const BACKGROUND_IMAGE_LOAD_TIMEOUT_MS = 8000

// Resolves whether `url` is actually loadable *before* we ever mount
// Remotion's <Img>. We deliberately do not lean on <Img>'s own
// onError/retry plumbing: once its internal retry budget is exhausted it
// only invokes the onError callback and never calls continueRender() on
// its own internally-created delayRender() handle, so a permanently dead
// URL would hang the render until Remotion's default 30s delayRender
// timeout fires cancelRender() and fails the whole render. Preloading
// with a plain `Image()` and owning our own delayRender/continueRender
// pair lets us resolve to `false` on error/timeout and simply never
// render <Img>, which is the documented-safe way to degrade to the black
// background without throwing.
function useBackgroundImageReady(url: string | null | undefined): boolean {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!url) {
      setReady(false)
      return
    }

    let finished = false
    const handle = delayRender('Preloading SubtitlePreview background image')
    const img = new Image()

    const finish = (ok: boolean): void => {
      if (finished) {
        return
      }
      finished = true
      setReady(ok)
      continueRender(handle)
    }

    img.onload = () => finish(true)
    img.onerror = () => finish(false)
    img.src = url

    const timeoutId = setTimeout(() => finish(false), BACKGROUND_IMAGE_LOAD_TIMEOUT_MS)

    return () => {
      clearTimeout(timeoutId)
      img.onload = null
      img.onerror = null
      // If we unmount mid-flight, make sure the delayRender handle is
      // always released so it can never dangle past this component's
      // lifetime.
      if (!finished) {
        finished = true
        continueRender(handle)
      }
    }
  }, [url])

  return ready
}

const TRANSCRIPTION: Transcription = {
  segments: [
    {
      id: 0,
      text: 'This is an example of',
      words: [
        {
          word: 'This',
          endTime: '00:00:00,340',
          startTime: '00:00:00,000',
          probability: 0.8194113373756409,
        },
        {
          word: 'is',
          endTime: '00:00:00,460',
          startTime: '00:00:00,340',
          probability: 0.9071844816207886,
        },
        {
          word: 'an',
          endTime: '00:00:00,580',
          startTime: '00:00:00,460',
          probability: 0.9078640937805176,
        },
        {
          word: 'example',
          endTime: '00:00:00,960',
          startTime: '00:00:00,580',
          probability: 0.9319542646408081,
        },
        {
          word: 'of',
          endTime: '00:00:01,200',
          startTime: '00:00:00,960',
          probability: 0.9085198044776917,
        },
      ],
      endTime: '00:00:01,200',
      startTime: '00:00:00,000',
    },
    {
      id: 1,
      text: ' subtitle animation',
      words: [
        {
          word: ' subtitle',
          endTime: '00:00:01,800',
          startTime: '00:00:01,200',
          probability: 0.9454399615526199,
        },
        {
          word: ' animation',
          endTime: '00:00:02,800',
          startTime: '00:00:01,800',
          probability: 0.9051726579666138,
        },
      ],
      endTime: '00:00:02,800',
      startTime: '00:00:01,200',
    },
  ],
}

const FORBIDDEN_ATTRIBUTES = new Set([
  'project.subtitle.lineStyle.fontSize',
  'project.subtitle.lineStyle.verticalMargin',
  'project.subtitle.lineStyle.horizontalMargin',
  'project.subtitle.lineStyle.justifyContent',
  'project.subtitle.lineStyle.alignItems',
])

const isForbiddenAttribute = (attribute: string): boolean => FORBIDDEN_ATTRIBUTES.has(attribute)

export default function VideoWithFonts({
  preset,
  backgroundImageUrl = null,
  canvasBackgroundColor = 'black',
  curve,
}: VideoWithFontsProps): JSX.Element | null {
  const { height } = useVideoConfig()
  // The real render forces captions into the lower area of frame when a
  // background is present, to mirror actual on-video placement. Preset ops
  // are applied after baseStyles below (and still win), so this is only
  // the *default* the user sees before/absent an overriding op.
  const hasBackgroundImage = Boolean(backgroundImageUrl)
  const maxFontSize: number = hasBackgroundImage ? height / 14 : height / 2.4
  const [waitForFont] = useState(() => delayRender())
  const backgroundImageReady = useBackgroundImageReady(backgroundImageUrl)

  const subtitle = useMemo(() => {
    const baseStyles = {
      highlightStyle: {
        fontColor: '#0DFDE8',
      },
      minSilenceToCut: 0.5,
      highlightBoxStyle: {
        enabled: false,
        paddingX: 0.3,
        paddingY: 0.05,
        borderRadius: 0.2,
        backgroundColor: '#4023d1',
      },
      keepOnlySubtitles: true,
      extraTimeTolerance: 0.75,
      minSubtitleSilenceToCut: 0.5,
      animation: null,
      lineStyle: {
        padding: 0.5,
        fontName: 'Komika Title Axis',
        fontColor: '#ebe9f1',
        lineHeight: 1,
        shadowSize: 11,
        shadowColor: '#050505',
        borderRadius: 0.25,
        textTransform: 'uppercase',
        backgroundColor: 'black',
        enableBackground: false,
        highlightCurrentWord: true,
        fontSize: maxFontSize,
        verticalMargin: hasBackgroundImage ? 12 : 0,
        horizontalMargin: hasBackgroundImage ? 10 : 5,
        paddingX: 0.3,
        paddingY: 0.5,
        gap: 0.3,
        justifyContent: 'center',
        alignItems: hasBackgroundImage ? 'flex-end' : 'center',
      },
      transcription: TRANSCRIPTION,
      reorganizeOptions: {
        maxWordsPerLine: 4,
        maxSecondsPerLine: 20.0,
        maxCharactersPerLine: 50,
        cutPhrases: false,
        removePunctuation: false,
      }
    }

    for (const {type, attribute, value, settings} of Object.values(preset)) {
      if (isForbiddenAttribute(attribute)) {
        continue
      }

      if (type === 'set-value') {
        lodash.set(baseStyles, attribute.replace('project.subtitle.', ''), value)
      } else if (type === 'reorganize-words') {
        if (settings) {
          baseStyles.reorganizeOptions = settings
        }
      }
    }

    return baseStyles
  }, [preset, height, maxFontSize, hasBackgroundImage])

  useEffect(() => {
    continueRender(waitForFont)
  }, [waitForFont])

  return (
    <AbsoluteFill style={{ backgroundColor: canvasBackgroundColor }}>
      {backgroundImageReady && backgroundImageUrl ? (
        <>
          <Img
            src={backgroundImageUrl}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            // Defense in depth only: by the time backgroundImageReady is
            // true, a plain Image() has already confirmed this exact URL
            // loads, so <Img> failing here would be an unlikely late
            // regression (e.g. the resource disappearing mid-render)
            // rather than the expected-dead-URL case, which is handled by
            // useBackgroundImageReady() before <Img> ever mounts.
            onError={() => {}}
          />
          {/* Readability scrim: keeps light caption text legible over
              bright thumbnails without hiding the image entirely. */}
          <AbsoluteFill
            style={{ background: 'linear-gradient(transparent 40%, rgba(0,0,0,0.55))' }}
          />
        </>
      ) : null}
      <Subtitle subtitle={subtitle as unknown as SubtitleType} scaleDesignPixels={false} curve={curve} />
    </AbsoluteFill>
  )
}
